// File: src/pages/buyer/AddProduct/sections/TechnicalSpecifications.jsx
import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Row, Col, Form, Button } from "react-bootstrap";
import HazardModal from "../../../../components/seller/HazardModal";
import { useParams } from "react-router-dom";

const TechnicalSpecifications = ({ isMetal = true }) => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const hazard = watch("hazard_types") || [];
  const hazardDesc = watch("hazard_description");

  const [modalVisible, setModalVisible] = useState(false);
  const [hazardDetails, setHazardDetails] = useState("");

  useEffect(() => {
    if (isEditMode && hazard.includes("hazardous") && hazardDesc) {
      setHazardDetails(hazardDesc);
    }
  }, [isEditMode, hazard, hazardDesc]);

  const handleSave = () => {
    setValue("hazard_description", hazardDetails || "");
    setModalVisible(false);
  };

  const handleModalClose = () => {
    // Close modal
    setModalVisible(false);

    // If hazardous is selected but user didn't save → uncheck it
    if (hazard.includes("hazardous") && !hazardDetails) {
      setValue("hazard_types", []); // uncheck both
      setValue("hazard_description", ""); // clear description
    }
  };

  const handleEditClick = () => {
    // Open the modal when clicking "Edit" if hazard description exists
    if (hazardDesc) {
      setHazardDetails(hazardDesc);
      setModalVisible(true);
    }
  };

  const handleHazardCheckbox = (optionId, checked) => {
    // If 'checked' is true, update the hazard array to only include the selected checkbox
    if (checked) {
      setValue("hazard_types", [optionId], { shouldValidate: true }); // Set only the selected checkbox
    } else {
      // If unchecked, remove it from the hazard array
      setValue(
        "hazard_types",
        hazard.filter((h) => h !== optionId),
        { shouldValidate: true }
      );
    }

    if (optionId === "hazardous" && checked) {
      // Open modal if no description exists
      if (!hazardDesc) {
        setHazardDetails("");
        setModalVisible(true);
      }
    }

    if (!checked && optionId === "hazardous") {
      // Clearing hazard description when unchecked
      setValue("hazard_description", "");
      setHazardDetails("");
    }
  };

  return (
    <div className="border rounded p-4 mb-4 bg-white">
      <h5 className="fw-semibold mb-4">Technical Specifications</h5>

      {/* First Row */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="text"
              inputMode="decimal"
              placeholder="Enter quantity"
              value={watch("quantity") || ""}
              {...register("quantity", {
                validate: (value) =>
                  !value ||
                  /^\d+(\.\d{0,3})?$/.test(value) ||
                  "Up to 3 decimal places allowed",
              })}
              onChange={(e) => {
                const value = e.target.value;

                // allow empty, numbers, and up to 3 decimals ONLY
                if (/^\d*(\.\d{0,3})?$/.test(value)) {
                  setValue("quantity", value, { shouldValidate: true });
                }
              }}
            />
            {errors.quantity && (
              <div className="text-danger small">{errors.quantity.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Unit Type</Form.Label>
            <Controller
              name="unit_of_measurement"
              control={control}
              rules={{ required: "Unit is required" }}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Select</option>
                  <option value="toz">Troy Ounce</option>
                  <option value="mt">Metric Tonne</option>
                  <option value="kg">Kg</option>
                  <option value="g">Gram</option>
                </Form.Select>
              )}
            />
            {errors.unit_of_measurement && (
              <div className="text-danger small">{errors.unit_of_measurement.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Sorting Type</Form.Label>
            <Controller
              name="sorting_type"
              control={control}
              rules={{ required: "Sorting type is required" }}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Select</option>
                  <option value="sorted">Sorted</option>
                  <option value="unsorted">Unsorted</option>
                </Form.Select>
              )}
            />
            {errors.sorting_type && (
              <div className="text-danger small">{errors.sorting_type.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Attachment (Waste Type)</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Plastic residue etc."
              {...register("attachment")}
            />
            {errors.attachment && (
              <div className="text-danger small">{errors.attachment.message}</div>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Second Row */}
      <Row className="g-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Waste % Range</Form.Label>
            <Controller
              name="waste_range"
              control={control}
              rules={{ required: "Waste range is required" }}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Select</option>
                  <option value="0-2">0–2%</option>
                  <option value="2-5">2–5%</option>
                  <option value="5-10">5–10%</option>
                  <option value="10+">10%+</option>
                </Form.Select>
              )}
            />
            {errors.waste_range && (
              <div className="text-danger small">{errors.waste_range.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Hazard Declaration</Form.Label>

            <div className="d-flex gap-3 flex-wrap align-items-center">
              {[
                { id: "non-hazardous", label: "Non-Hazardous" },
                { id: "hazardous", label: "Hazardous" },
              ].map((option) => (
                <Form.Check
                  key={option.id}
                  type="checkbox"
                  label={option.label}
                  id={option.id}
                  checked={hazard.includes(option.id)}
                  onChange={(e) =>
                    handleHazardCheckbox(option.id, e.target.checked)
                  }
                />
              ))}

              {hazard.includes("hazardous") && hazardDesc && (
                <Button
                  variant="link"
                  className="p-0 text-decoration-underline"
                  onClick={handleEditClick}
                >
                  Edit details
                </Button>
              )}
            </div>
            {errors.hazard_types && (
              <div className="text-danger small mt-1">{errors.hazard_types.message}</div>
            )}
          </Form.Group>
        </Col>

        {/* Commented out for future use: Toxic level and Weight */}
        {/* 
        <Col md={3}>
          <Form.Group>
            <Form.Label>Toxic Level</Form.Label>
            <Controller
              name="toxic_level"
              control={control}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Select</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </Form.Select>
              )}
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Weight (kg)</Form.Label>
            <Form.Control type="number" {...register("weight")} min={0} />
          </Form.Group>
        </Col>
        */}
      </Row>

      <HazardModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSave={handleSave}
        textValue={hazardDetails}
        onTextChange={setHazardDetails}
      />
    </div>
  );
};

export default React.memo(TechnicalSpecifications);

// {
//   /* <CCol md={3}>
//           <CFormLabel>Toxic Level</CFormLabel>
//           <Controller
//             name="toxic_level"
//             control={control}
//             render={({ field }) => (
//               <CFormSelect
//                 {...field}
//                 options={[
//                   { label: "Select", value: "" },
//                   { label: "Low", value: "Low" },
//                   { label: "Medium", value: "Medium" },
//                   { label: "High", value: "High" },
//                 ]}
//               />
//             )}
//           />
//         </CCol> */
// }

// {
//   /* Hazard Type moved to last column */
// }

// {
//   /*
//         <CCol md={3}>
//           <CFormLabel>Weight (kg)</CFormLabel>
//           <CFormInput type="number" {...register("weight")} min={0} />
//           {errors.weight && (
//             <div style={{ color: "red" }}>{errors.weight.message}</div>
//           )}
//         </CCol>
//         */
// }
